import { Flex } from "@/components/Flex";
import { iconSizes, strokeWidth } from "@/utils/iconProps";
import { themeColors } from "@/utils/themeColors";
import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import Box from "@mui/material/Box";
import { SxProps } from "@mui/material/styles";
import { Check, ChevronDown } from "lucide-react";
import React, { useState } from "react";
import Circular from "../Circular";
import Mountable from "../styled/div/Mountable";

export type OptionProps<T> = {
    label: string;
    value: T;
    node?: React.ReactNode;
};

export type MultiInputWithMenuProps<T> = {
    values: OptionProps<T>[] | null;
    options: OptionProps<T>[];
    onChange: (values: { value: T; label: string }[], clickedValue: { value: T; label: string }) => void;
    rootProps?: React.HTMLAttributes<HTMLDivElement>;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement> & { sx?: SxProps };
    ulProps?: React.HTMLAttributes<HTMLUListElement> & { sx?: SxProps };
    liProps?: React.HTMLAttributes<HTMLLIElement> & { sx?: SxProps };
    adornment?: React.ReactNode;
    renderLabel?: (props: { values: { value: T; label: string }[] }) => string;
};

/**
 * Just like input-with-menu but support for multiple values
 * - `ulProps` is props of the listbox (suggestion.ts menu body)
 * - `liProps` is props of the listbox option (item within the suggestion.ts menu body)
 */
const MultiInputWithMenu = <T,>({
    values,
    options,
    onChange,
    rootProps,
    inputProps,
    ulProps,
    liProps,
    adornment = <ChevronDown size={"1.8rem"} strokeWidth={strokeWidth.normal} />,
    renderLabel = (props: { values: OptionProps<T>[] }) => props.values.map(v => v.label).join(", "),
}: MultiInputWithMenuProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);

    const isValueSelected = (val: OptionProps<T>) => {
        return !!values && values.find(v => v.value === val.value) !== undefined;
    };

    const renderOptions = () => {
        return (
            <Ul {...ulProps} data-testid="auto-id-bac2ceb2-1971-41c6-af46-c6edb3795355">
                {options.map((option, index) => (
                    <Li
                        {...liProps}
                        onClick={() => {
                            if (!values) return;

                            if (isValueSelected(option)) {
                                const newValue = values.filter(v => v.value !== option.value);
                                onChange(newValue, option);
                            } else {
                                onChange([...values, option], option);
                            }
                        }}
                        isChecked={!!values && !!values.find(v => v.value === option.value)}
                        key={index.toString() + option.value + option.label}
                        data-testid="auto-id-2a77ad7c-738c-4c26-9104-3d331b9aa25b">
                        {option.node || option.label}
                    </Li>
                ))}
            </Ul>
        );
    };

    return (
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
            <div
                {...rootProps}
                style={{ position: "relative", ...rootProps?.style }}
                data-testid="auto-id-26bde2db-ff16-4f25-924d-3bcc4cdc7ee4">
                <Input
                    {...inputProps}
                    readOnly
                    onFocus={() => setIsOpen(true)}
                    sx={{ ...inputProps?.sx, paddingRight: "4rem" }}
                    value={renderLabel({ values: values || [] })}
                    data-testid="auto-id-e97fb3ee-de5e-4648-8fd7-007e734a9ebd" />

                <Mountable mount={!!adornment}>
                    <Flex style={{ position: "absolute", right: "1.5rem", top: "50%", transform: "translateY(-50%)" }}>
                        {adornment}
                    </Flex>
                </Mountable>

                {isOpen && renderOptions()}
            </div>
        </ClickAwayListener>
    );
};

export default MultiInputWithMenu;

const Input = ({ ref, sx, ...props }) => {
    return (
        <Box
            ref={ref}
            component={"input"}
            {...props}
            sx={{
                width: "100%",
                fontFamily: "var(--knowt-font)",
                backgroundColor: themeColors.background,
                color: themeColors.neutralBlack,
                outline: "none",
                border: "none",
                padding: 0,
                textOverflow: "ellipsis",
                ...sx,
            }}
        />
    );
};

Input.displayName = "Input";

const Ul = ({ ref, sx, ...props }) => {
    return (
        <Box
            ref={ref}
            component={"ul"}
            {...props}
            sx={{
                width: "100%",
                zIndex: 1,
                position: "absolute",
                backgroundColor: themeColors.background,
                overflow: "auto",
                fontSize: "inherit",
                maxHeight: "18rem",
                "& li.Mui-focused": {
                    color: themeColors.primary,
                    cursor: "pointer",
                },
                "& li:hover": {
                    color: themeColors.primary,
                },
                listStyle: "none",
                padding: "0.64rem 0",
                margin: 0,
                ...sx,
            }}
        />
    );
};

Ul.displayName = "Ul";

const Li = ({ ref, children, sx, isChecked, ...props }) => {
    return (
        <Box
            ref={ref}
            component={"li"}
            {...props}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "1.2rem",
                padding: "0.32rem 1.28rem",
                margin: "0.2rem",
                cursor: "pointer",
                ...sx,
            }}>
            <Checkbox isChecked={isChecked} />
            <span
                className="ellipsisText"
                data-testid="auto-id-9fd26c3f-ac98-48f0-b43a-baf0d4859e90">{children}</span>
        </Box>
    );
};

Li.displayName = "Li";

const Checkbox = ({ isChecked }: { isChecked: boolean }) => {
    return (
        <Circular radius="2rem" style={{ borderRadius: "0.5rem", border: `2px solid ${themeColors.neutral3}` }}>
            {isChecked && <Check size={iconSizes.XS} color={themeColors.neutralBlack} strokeWidth={strokeWidth.bold} />}
        </Circular>
    );
};
